import React, { useEffect, useState } from "react";
import {
    BookOpenText,
    CalendarCheck2,
    ChevronLeft,
    ChevronRight,
    Info,
    Layers3,
    MapPin,
    MonitorPlay,
    Users,
    X,
} from "lucide-react";

import "../../RessourcesStyles/EventManagement.css";

const PARTICIPANTS_PAGE_SIZE = 5;

const statusColor = (status) => ({
    PLANIFIE: { bg: "#e8edf7", color: "#204385", label: "Planifié" },
    EN_COURS: { bg: "#d1fae5", color: "#059669", label: "En cours" },
    TERMINE: { bg: "#f3f4f6", color: "#6b7280", label: "Terminé" },
    CLOTURE: { bg: "#f3f4f6", color: "#6b7280", label: "Clôturé" },
    ANNULE: { bg: "#fde8e7", color: "#ee645f", label: "Annulé" },
}[status] || { bg: "#f3f4f6", color: "#6b7280", label: status || "-" });

const formatDate = (value, withTime = false) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return new Intl.DateTimeFormat("fr-FR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
    }).format(date);
};

const hasValue = (value) =>
    value !== null &&
    value !== undefined &&
    String(value).trim() !== "" &&
    String(value).trim() !== "-";

const getDisplayValue = (...values) => values.find((value) => value !== null && value !== undefined && value !== "") || "-";

const getInitials = (name) => {
    if (!name || name === "-") return "?";
    const parts = name.split(" ").filter(Boolean);
    if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return parts[0] ? parts[0][0].toUpperCase() : "?";
};

const getParticipantName = (participant) =>
    getDisplayValue(
        participant?.fullName,
        `${participant?.firstName || participant?.prenom || ""} ${participant?.lastName || participant?.nom || ""}`.trim(),
        participant?.username,
        participant?.login
    );

const getLocationLabel = (e) => {
    if (!e) return "";
    if (e.locationType === "VIRTUELLE") return e.offsiteLocation || e.salle?.nom || "Lien visio";
    if (e.locationType === "OFFSITE") return e.offsiteLocation || "Lieu externe";
    return e.salle?.nom || e.offsiteLocation || "";
};

export default function EventDetailsModal({ event, onClose }) {
    const [participantsPage, setParticipantsPage] = useState(1);

    useEffect(() => {
        if (event) {
            setParticipantsPage(1);
        }
    }, [event?.id]);

    useEffect(() => {
        if (!event) return undefined;
        const onKeyDown = (evt) => { if (evt.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [event, onClose]);

    if (!event) return null;

    const consultants = Array.isArray(event.consultants) ? event.consultants : [];
    const sessions = Array.isArray(event.sessions) ? event.sessions : [];
    const participants = Array.isArray(event.participants) ? event.participants : [];

    const statusStyle = statusColor(event.status);
    const periodLabel = `${formatDate(event.dateDebut, true)} → ${formatDate(event.dateFin, true)}`;
    const approvedRegistrationsCount = event.approvedRegistrationsCount || 0;
    console.log("evevevve",event)
    const participantPages = Math.max(1, Math.ceil(participants.length / PARTICIPANTS_PAGE_SIZE));
    const paginatedParticipants = participants.slice(
        (participantsPage - 1) * PARTICIPANTS_PAGE_SIZE,
        participantsPage * PARTICIPANTS_PAGE_SIZE
    );

    const trainingType = getDisplayValue(event.moduleCle?.nom, event.moduleCleNom);

    // Calcul de l'affichage des 3 hero cards max
    const showLieuCard = hasValue(event.salle?.nom) || hasValue(event.offsiteLocation);
    const participantCount = (participants && participants.length > 0) ? participants.length : approvedRegistrationsCount;
    const participantsLabel = event.maxPlaces ? `${participantCount} / ${event.maxPlaces}` : String(participantCount);

    const registrationStatusText = (() => {
        const status = event.status;
        if (status === "TERMINE") return "Terminées";
        if (status === "ANNULE") return "Annulées";
        if (event.allowRegistration === false || event.allowRegistration === null || event.allowRegistration === undefined) return "Non disponible";
        if (event.registrationOpen) return "Ouvertes";
        return "Fermées";
    })();

    // Gestion du formateur et de la durée
    const sessionsHaveTrainer = sessions.some(s => hasValue(s.consultant?.nomContact) || hasValue(s.consultantName));
    const showConsultantsInline = !sessionsHaveTrainer && consultants.length > 0;
    const showDurationColumn = sessions.some(s => hasValue(s.dureeHours));

    const remainingPlaces = event.maxPlaces ? Math.max(0, event.maxPlaces - approvedRegistrationsCount) : null;

    return (
        <div className="ev-modal-overlay" onClick={onClose}>
            <div className="ev-modal ev-modal--large ev-details-modal" onClick={(evt) => evt.stopPropagation()}>
                
                {/* ── 1. EN-TETE ────────────────────────────────── */}
                <div className="ev-modal-header ev-details-modal__header">
                    <div className="ev-details-modal__title-wrap">
                        <div className="ev-details-modal__eyebrow">Détails de l'événement</div>
                        <div className="ev-details-modal__head-main">
                            <h3>{event.titre || "Événement"}</h3>
                            <span className="ev-details-modal__status" style={{ background: statusStyle.bg, color: statusStyle.color }}>
                                {statusStyle.label}
                            </span>
                        </div>
                        <div className="ev-details-modal__date">{periodLabel}</div>
                    </div>
                    <button type="button" className="ev-x" onClick={onClose} aria-label="Fermer">
                        <X size={18} />
                    </button>
                </div>

                <div className="ev-modal-body ev-details-modal__body">
                    
                    {/* ── 2. BLOC RESUME RAPIDE ──────────────────── */}
                    <div className="ev-details-hero">
                        {showLieuCard && (
                            <div className="ev-details-hero__item">
                                {event.locationType === "VIRTUELLE" ? <MonitorPlay size={16} /> : <MapPin size={16} />}
                                <div>
                                    <span>Lieu</span>
                                    <strong>{getLocationLabel(event)}</strong>
                                </div>
                            </div>
                        )}
                        <div className="ev-details-hero__item">
                            <Users size={16} />
                            <div>
                                <span>Participants</span>
                                <strong>{participantsLabel}</strong>
                            </div>
                        </div>
                        <div className="ev-details-hero__item">
                            <CalendarCheck2 size={16} />
                            <div>
                                <span>Inscriptions</span>
                                <strong className={registrationStatusText === "Ouvertes" ? "ev-details-hero__value--success" : "ev-details-hero__value--danger"}>
                                    {registrationStatusText}
                                </strong>
                            </div>
                        </div>
                    </div>

                    <div className="ev-details-grid">
                        
                        {/* ── A. INFORMATIONS ────────────────────── */}
                        <section className="ev-box ev-details-section">
                            <div className="ev-details-section__title">
                                <BookOpenText size={16} />
                                <span>Informations</span>
                            </div>
                            <div className="ev-details-list">
                                {hasValue(event.description) && (
                                    <div className="ev-details-list__row ev-details-list__row--full">
                                        <span>Description</span>
                                        <p>{event.description}</p>
                                    </div>
                                )}
                                {hasValue(trainingType) && (
                                    <div className="ev-details-list__row">
                                        <span>Formation / module</span>
                                        <strong>{trainingType}</strong>
                                    </div>
                                )}
                                {hasValue(event.moduleCle?.reference) && (
                                    <div className="ev-details-list__row">
                                        <span>Référence module</span>
                                        <strong>{event.moduleCle.reference}</strong>
                                    </div>
                                )}
                                {hasValue(event.campagneReference) && (
                                    <div className="ev-details-list__row">
                                        <span>Campagne</span>
                                        <strong>{event.campagneReference}</strong>
                                    </div>
                                )}
                                {showConsultantsInline && (
                                    <div className="ev-details-list__row ev-details-list__row--full">
                                        <span>Formateur(s)</span>
                                        <strong>
                                            {consultants
                                                .map((c) => c.nomContact || c.fullName || `${c.firstName || ""} ${c.lastName || ""}`.trim())
                                                .filter(Boolean)
                                                .join(", ")}
                                        </strong>
                                    </div>
                                )}
                            </div>
                        </section>

                        {/* ── B. ORGANISATION ───────────────────── */}
                        <section className="ev-box ev-details-section">
                            <div className="ev-details-section__title">
                                <Info size={16} />
                                <span>Organisation</span>
                            </div>
                            <div className="ev-details-list">
                                {hasValue(event.salle?.nom) && (
                                    <div className="ev-details-list__row">
                                        <span>Salle</span>
                                        <strong>{event.salle.nom}</strong>
                                    </div>
                                )}
                                {hasValue(event.salle?.type) && (
                                    <div className="ev-details-list__row">
                                        <span>Type de salle</span>
                                        <strong>{event.salle.type}</strong>
                                    </div>
                                )}
                                {hasValue(event.registrationDeadlineAt) && (
                                    <div className="ev-details-list__row">
                                        <span>Date limite d'inscription</span>
                                        <strong>{formatDate(event.registrationDeadlineAt, true)}</strong>
                                    </div>
                                )}
                                {hasValue(event.maxPlaces) && (
                                    <div className="ev-details-list__row">
                                        <span>Capacité</span>
                                        <strong>{event.maxPlaces} places</strong>
                                    </div>
                                )}
                                {remainingPlaces !== null && (
                                    <div className="ev-details-list__row">
                                        <span>Places restantes</span>
                                        <strong>{remainingPlaces}</strong>
                                    </div>
                                )}
                                <div className="ev-details-list__row">
                                    <span>Statut inscription</span>
                                    <strong className={registrationStatusText === "Ouvertes" ? "ev-details-hero__value--success" : "ev-details-hero__value--danger"}>
                                        {registrationStatusText}
                                    </strong>
                                </div>
                            </div>
                        </section>

                        {/* ── C. DEROULEMENT / SEANCES ────────────────────────── */}
                        <section className="ev-box ev-details-section ev-details-section--full">
                            <div className="ev-details-section__title">
                                <Layers3 size={16} />
                                <span>Déroulement / séances</span>
                            </div>
                            {sessions.length ? (
                                <div className="ev-table-wrap">
                                    <table className="ev-table ev-table--sm">
                                        <thead>
                                            <tr>
                                                <th>Séance</th>
                                                {showDurationColumn && <th>Durée</th>}
                                                <th>Horaires</th>
                                                <th>Salle</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sessions.map((session, index) => {
                                                const sessionRoom = getDisplayValue(event.salle?.nom, session.location);
                                                const sessionTrainer = getDisplayValue(session.consultant?.nomContact, session.consultantName);
                                                return (
                                                    <tr key={session.id || `${session.name}-${index}`}>
                                                        <td><strong>{session.name || `Séance ${index + 1}`}</strong></td>
                                                        {showDurationColumn && <td>{hasValue(session.dureeHours) ? `${session.dureeHours} h` : ""}</td>}
                                                        <td className="ev-details-table__compact-cell">
                                                            {formatDate(session.startat, true)} - {formatDate(session.endat, true)}
                                                        </td>
                                                        <td>{hasValue(sessionRoom) ? sessionRoom : ""}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            ) : <div className="ev-details-empty">Aucune séance planifiée.</div>}
                        </section>

                        {/* ── D. PARTICIPANTS / PERSONNES AFFECTEES ────────────────────── */}
                        <section className="ev-box ev-details-section ev-details-section--full">
                            <div className="ev-details-section__header">
                                <div className="ev-details-section__title">
                                    <Users size={16} />
                                    <span>Participants / personnes affectées</span>
                                </div>
                                {participants.length ? (
                                    <div className="ev-details-section__meta">
                                        <span>{participants.length} personnes</span>
                                    </div>
                                ) : null}
                            </div>
                            {participants.length ? (
                                <>
                                    <div className="ev-details-participants-list">
                                        {paginatedParticipants.map((participant) => {
                                            const fullName = getParticipantName(participant);
                                            const initials = getInitials(fullName);
                                            const metaInfo = [
                                                participant.email,
                                                participant.matricule,
                                                participant.poste || participant.jobTitle
                                            ].filter(hasValue).join(" • ");
                                            
                                            const status = participant.status || participant.statut || participant.assignmentStatus;

                                            return (
                                                <div className="ev-details-participant-item" key={participant.assignmentId || participant.userId || participant.id}>
                                                    <div className="ev-details-participant-left">
                                                        <div className="ev-details-participant-avatar">
                                                            {initials}
                                                        </div>
                                                        <div className="ev-details-participant-info">
                                                            <span className="ev-details-participant-name">{fullName}</span>
                                                            {hasValue(metaInfo) && <span className="ev-details-participant-meta">{metaInfo}</span>}
                                                        </div>
                                                    </div>
                                                    {hasValue(status) && (
                                                        <span className="ev-details-participant-status">
                                                            {status}
                                                        </span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {participantPages > 1 ? (
                                        <div className="ev-details-pagination">
                                            <button
                                                type="button"
                                                className="ev-details-pagination__btn"
                                                onClick={() => setParticipantsPage((page) => Math.max(1, page - 1))}
                                                disabled={participantsPage === 1}
                                            >
                                                <ChevronLeft size={14} />
                                                <span>Précédent</span>
                                            </button>
                                            <div className="ev-details-pagination__status">
                                                {(participantsPage - 1) * PARTICIPANTS_PAGE_SIZE + 1}
                                                -
                                                {Math.min(participantsPage * PARTICIPANTS_PAGE_SIZE, participants.length)} sur {participants.length}
                                            </div>
                                            <button
                                                type="button"
                                                className="ev-details-pagination__btn"
                                                onClick={() => setParticipantsPage((page) => Math.min(participantPages, page + 1))}
                                                disabled={participantsPage === participantPages}
                                            >
                                                <span>Suivant</span>
                                                <ChevronRight size={14} />
                                            </button>
                                        </div>
                                    ) : null}
                                </>
                            ) : <div className="ev-details-empty">Aucun participant inscrit pour le moment.</div>}
                        </section>
                    </div>
                </div>
            </div>
        </div>
    );
}
